import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Spinner from '../../util/spinner-shim'
import { fromC } from 'altrac-conversions'

import AppDispatcher from '../../dispatchers/AppDispatcher'

// internal libs
import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import withAppStore from '../../util/withAppStore'

// icons
import AltracBattery from '../partials/Graphics/AltracBattery'
import BatteryDefault from '../partials/Graphics/Battery'
import Bin from '../partials/Graphics/Bin'
import ColdAirDrain from '../partials/Graphics/ColdAirDrain'
import Flow from '../partials/Graphics/Flow'
import Fuel from '../partials/Graphics/Fuel'
import High from '../partials/Graphics/High'
import Low from '../partials/Graphics/Low'
import Moisture from '../partials/Graphics/Moisture'
import PowerDisconnected from '../partials/Graphics/PowerDisconnected'
import Pressure from '../partials/Graphics/Pressure'
import Pump from '../partials/Graphics/Pump'
import PumpFrostWater from '../partials/Graphics/PumpFrostWater'
import PlusCircle from '../partials/Graphics/PlusCircle'
import { SpeedometerOff, SpeedometerOn } from '../partials/Graphics/Speedometer'
import Thermometer from '../partials/Graphics/Thermometer'
import WindMachine from '../partials/Graphics/WindMachine'
import Valve from '../partials/Graphics/Valve'

import styles from '../../../css/devicePage.module.css'

// dictionary of templates used to create the buttons
const templates = {
  batteryExternal: {
    value: 'batteryExternal',
    message: {
      id: 'rules.battery.external.below',
      defaultMessage: 'Battery voltage below {testValue}{unit}',
    },
    icon: <BatteryDefault low />,
  },
  batteryExternalDisconnected: {
    value: 'batteryExternalDisconnected',
    message: {
      id: 'rules.battery.external.disconnected.alert',
      defaultMessage: 'Battery disconnected',
    },
    icon: <BatteryDefault disconnected />,
  },
  batteryExternalLessThan: {
    value: 'batteryExternalLessThan',
    message: {
      id: 'rules.battery.external.below',
      defaultMessage: 'Battery voltage below {testValue}{unit}',
    },
    icon: <BatteryDefault low />,
  },
  batteryInternal: {
    value: 'batteryInternal',
    message: {
      id: 'rules.battery.internal.lessThan20.alert',
      defaultMessage: 'Altrac Battery below 20%',
    },
    icon: <AltracBattery low />,
  },
  batteryInternalHigh: {
    value: 'batteryInternalHigh',
    message: {
      id: 'rules.battery.internal.high.alert',
      defaultMessage: 'Altrac Battery above {testValue}%',
    },
    icon: <AltracBattery high />,
  },
  bindicator: {
    value: 'bindicatorGreaterThan',
    message: {
      id: 'rules.bins.full.alert',
      defaultMessage: '{testValue}',
    },
    icon: <Bin high />,
  },
  bindicatorGreaterThan: {
    value: 'bindicatorGreaterThan',
    message: {
      id: 'rules.bins.gt.alert',
      defaultMessage: '{testValue}',
    },
    icon: <Bin high />,
  },
  bindicatorLessThan: {
    value: 'bindicatorLessThan',
    message: {
      id: 'rules.bins.lt.alert',
      defaultMessage: '{testValue}',
    },
    icon: <Bin low />,
  },
  differenceHigh: {
    value: 'differenceHigh',
    message: {
      id: 'rules.pressure.differential.high.alert',
      defaultMessage: 'Pressure Differential above {testValue}{unit}',
    },
    icon: <Pressure high />,
  },
  engineRunningStart: {
    value: 'engineRunningStart',
    message: {
      id: 'rules.machineState.running.alert',
      defaultMessage: 'Wind machine starting',
    },
    icon: <SpeedometerOn />,
  },
  engineRunningStop: {
    value: 'engineRunningStop',
    message: {
      id: 'rules.machineState.stopped.alert',
      defaultMessage: 'Wind machine stopping',
    },
    icon: <SpeedometerOff />,
  },
  engineFailedToStart: {
    value: 'engineFailedToStart',
    message: {
      id: 'rules.machineState.failedToStart.alert',
      defaultMessage: 'Wind machine failed to start',
    },
    icon: <SpeedometerOff />,
  },
  flowRateHigh: {
    value: 'flowRateHigh',
    message: {
      id: 'rules.flow.high.alert',
      defaultMessage: 'Flow Rate above {testValue}{unit}',
    },
    icon: <Flow high />,
  },
  flowRateLow: {
    value: 'flowRateLow',
    message: {
      id: 'rules.flow.low.alert',
      defaultMessage: 'Flow Rate below {testValue}{unit}',
    },
    icon: <Flow low />,
  },
  fuelLevelLessThan: {
    value: 'fuelLevelLessThan',
    message: {
      id: 'rules.fuel.below',
      defaultMessage: 'Alert me when fuel level is below {testValue}',
    },
    icon: (<Fuel low />),
  },
  general: {
    value: 'general',
    message: {
      id: 'rules.customeRule',
      defaultMessage: 'Custom Rule.',
    },
  },
  machineStateEqualTo: {
    value: 'machineStateEqualTo',
    message: {
      id: 'rules.machine.state.equalTo.alert',
      defaultMessage: 'Machine State equals {testValue}',
    },
  },
  machineStateGreaterThan: {
    value: 'machineStateGreaterThan',
    message: {
      id: 'rules.machine.state.greaterThan.alert',
      defaultMessage: 'Machine State above {testValue}',
    },
    icon: <High />,
  },
  machineStateLessThan: {
    value: 'machineStateLessThan',
    message: {
      id: 'rules.machine.state.lessThan.alert',
      defaultMessage: 'Machine State below {testValue}',
    },
    icon: <Low />,
  },
  pondLevelHigh: {
    value: 'pondLevelHigh',
    message: {
      id: 'rules.poneLevel.high.alert',
      defaultMessage: 'Pond Level above {testValue}{unit}',
    },
    icon: <High />,
  },
  pondLevelLow: {
    value: 'pondLevelLow',
    message: {
      id: 'rules.poneLevel.low.alert',
      defaultMessage: 'Pond Level below {testValue}{unit}',
    },
    icon: <Low />,
  },
  powerDisconnected: {
    value: 'powerDisconnected',
    message: {
      id: 'rules.power.disconnected',
      defaultMessage: 'Altrac Station Power disconnected',
    },
    icon: <PowerDisconnected />,
  },
  pressureGreaterThan: {
    value: 'pressureGreaterThan',
    message: {
      id: 'rules.pressure.high',
      defaultMessage: '{sensor} above {testValue}{unit}',
    },
    icon: <Pressure high />,
  },
  pressureLessThan: {
    value: 'pressureLessThan',
    message: {
      id: 'rules.pressure.low',
      defaultMessage: '{sensor} below {testValue}{unit}',
    },
    icon: <Pressure low />,
  },
  pressureHigh: {
    value: 'pressureHigh',
    message: {
      id: 'rules.pressure.high',
      defaultMessage: '{sensor} above {testValue}{unit}',
    },
    icon: <Pressure high />,
  },
  pressureLow: {
    value: 'pressureLow',
    message: {
      id: 'rules.pressure.low',
      defaultMessage: '{sensor} below {testValue}{unit}',
    },
    icon: <Pressure low />,
  },
  pumpRunning: {
    value: 'pumpRunning',
    message: {
      id: 'rules.pump.running.alert',
      defaultMessage: '{sensor} running',
    },
    icon: <Pump running />,
  },
  pumpStopped: {
    value: 'pumpStopped',
    message: {
      id: 'rules.pump.stopped.alert',
      defaultMessage: '{sensor} stopped',
    },
    icon: <Pump stopped />,
  },
  pumpShouldBeRunning: {
    value: 'pumpShouldBeRunning',
    message: {
      id: 'rules.pump.shouldBeRunning.alert',
      defaultMessage: '{sensor} should be running but not running',
    },
    icon: <Pump shouldBeRunning />,
  },
  temperatureFEqualTo: {
    value: 'temperatureFEqualTo',
    message: {
      id: 'rules.temperature.ambient.equals.alert',
      defaultMessage: 'Ambient temperature equals {testValue}{unit}',
    },
    icon: <Thermometer />,
  },
  temperatureFGreaterThan: {
    value: 'temperatureFGreaterThan',
    message: {
      id: 'rules.temperature.ambient.high.alert',
      defaultMessage: 'Ambient temperature above {testValue}{unit}',
    },
    icon: <Thermometer high />,
  },
  speed: {
    value: 'speed',
    message: {
      id: 'rules.speed.alert',
      defaultMessage: 'Speed {testValue} {unit}',
    },
    icon: <SpeedometerOn />,
  },
  temperatureFLessThan: {
    value: 'temperatureFLessThan',
    message: {
      id: 'rules.temperature.ambient.low.alert',
      defaultMessage: 'Ambient Temperature below {testValue}{unit}',
    },
    icon: <Thermometer low />,
  },
  temperatureGreaterThan: {
    value: 'temperatureGreaterThan',
    message: {
      id: 'rules.temperature.ambient.high.alert',
      defaultMessage: 'Ambient temperature above {testValue}{unit}',
    },
    icon: <Thermometer high />,
  },
  temperatureLessThan: {
    value: 'temperatureLessThan',
    message: {
      id: 'rules.temperature.ambient.low.alert',
      defaultMessage: 'Ambient Temperature below {testValue}{unit}',
    },
    icon: <Thermometer low />,
  },
  soilMoistureGreaterThan: {
    value: 'soilMoistureGreaterThan',
    message: {
      id: 'rules.soil.moisture.high.alert',
      defaultMessage: 'Combined Soil Moisture above {testValue}{unit}',
    },
    icon: <Moisture high />,
  },
  soilMoistureLessThan: {
    value: 'soilMoistureLessThan',
    message: {
      id: 'rules.soil.moisture.low.alert',
      defaultMessage: 'Combined Soil Moisture below {testValue}{unit}',
    },
    icon: <Moisture low />,
  },
  soilTemperatureGreaterThan: {
    value: 'soilTemperatureGreaterThan',
    message: {
      id: 'rules.soil.temperature.high.alert',
      defaultMessage: 'Combined Soil Temperature above {testValue}{unit}',
    },
    icon: <Thermometer />,
  },
  soilTemperatureLessThan: {
    value: 'soilTemperatureLessThan',
    message: {
      id: 'rules.soil.temperature.low.alert',
      defaultMessage: 'Combined Soil Temperature below {testValue}{unit}',
    },
    icon: <Thermometer />,
  },
  soilSalinityGreaterThan: {
    value: 'soilSalinityGreaterThan',
    message: {
      id: 'rules.soil.salinity.high.alert',
      defaultMessage: 'Combined Soil Salinity above {testValue}{unit}',
    },
    icon: <High />,
  },
  soilSalinityLessThan: {
    value: 'soilSalinityLessThan',
    message: {
      id: 'rules.soil.salinity.low.alert',
      defaultMessage: 'Soil Salinity below {testValue}{unit}',
    },
    icon: <Low />,
  },
}

function DeviceRulesPage (props) {
  const navigate = useNavigate()
  const { customerID, deviceID } = useParams()

  const [once, setOnce] = React.useState(true)

  const backToDevice = React.useCallback(() => {
    const path = `/${customerID}/devices/${deviceID}/settings`
    navigate(path)
  }, [customerID, deviceID])
  const getDevice = React.useCallback(() => {
    AppDispatcher.dispatch({
      action: 'get-device',
      deviceID,
      callback: () => getNewData(),
    })
  }, [deviceID])
  const getNewData = React.useCallback(() => {
    const {
      data: {
        lookupReady,
        lookup,
      } = {},
    } = props

    const device = lookupReady && lookup[deviceID]

    if (lookupReady && device) {
      AppDispatcher.dispatch({
        action: 'get-device-rules',
        address: device.address,
      })
    }
  }, [deviceID, props.data?.lookupReady, props.data?.lookup])
  const toDeviceAdmin = React.useCallback(() => {
    navigate(`/${customerID}/devices/${deviceID}/admin`)
  }, [customerID, deviceID])

  React.useEffect(() => {
    getDevice()
  }, [getDevice])

  React.useEffect(() => {
    const { data: { lookupReady, lookup } = {} } = props

    const device = lookupReady && lookup[deviceID]

    if (lookupReady && device && once) {
      setOnce(false)
      getNewData()
    }

    window.scrollTo(0, 0) // scroll to top of page
  }, [props.data?.lookupReady, props.data?.lookup, deviceID, once, getNewData])

  React.useEffect(() => {
    return () => {
      AppDispatcher.dispatch({
        action: 'unload-device-rules',
      })
    }
  }, [])

  const {
    auth: { logout },
    data: {
      lookupReady,
      lookup,
      rules: {
        rules,
        alertsReady,
      } = {},
    } = {},
    intl: {
      formatMessage,
    },
    route: {
      path,
    } = {},
    level,
    tempConv = 'f',
  } = props

  const device = lookupReady && lookup[deviceID]

  const {
    physical: {
      deviceType,
    } = {},
    interface_versioned: interfaceVersioned = {},
  } = device || {}

  let telemetryTypes = []

  if (interfaceVersioned) {
    ({
      primaryPage: {
        telemetryTypes = [],
      } = {},
    } = interfaceVersioned)
  }

  const {
    address_alias: addressAlias,
    physical: {
      deviceNumber,
    } = {},
  } = device || {}

  let rightText = ''
  let rightFunction

  if (level >= 20) {
    rightText = 'fa-wrench'
    rightFunction = toDeviceAdmin
  }

  const SOIL_MOISTURE_SENSOR = formatMessage({
    id: 'sensor.soil.moisture',
    defaultMessage: 'Soil Moisture',
    description: 'Soil Moisture label',
  })

  const SOIL_SALINITY_SENSOR = formatMessage({
    id: 'sensor.soil.salinity',
    defaultMessage: 'Soil Salinity',
    description: 'Soil Salinity label',
  })

  const SOIL_TEMPERATURE_SENSOR = formatMessage({
    id: 'sensor.soil.temperature',
    defaultMessage: 'Soil Temperature',
    description: 'Soil Temperature label',
  })

  const AMBIENT_TEMPERATURE = formatMessage({
    id: 'sensor.temperature.ambient.long',
    defaultMessage: 'Ambient Temperature',
    description: 'Ambient Temperature label',
  })

  const DIFFERENTIAL = formatMessage({
    id: 'sensor.pump.differential',
    defaultMessage: 'Pressure Differential',
    description: 'filter pressure differential label',
  })

  const EXTERNAL_BATTERY = formatMessage({
    id: 'sensor.battery.external',
    defaultMessage: 'External Battery',
    description: 'External Battery label',
  })

  const INTERNAL_BATTERY = formatMessage({
    id: 'sensor.battery.internal',
    defaultMessage: 'Internal Battery',
    description: 'Internal Battery label',
  })

  const MACHINE_STATE = formatMessage({
    id: 'device.machineState.name',
    defaultMessage: 'Machine State',
    description: 'Machine State label',
  })

  const MACHINE_SPEED = formatMessage({
    id: 'sensor.speed',
    defaultMessage: 'Speed',
    description: 'Speed label',
  })

  const OFF = formatMessage({
    id: 'device.machineState.off',
    defaultMessage: 'OFF',
    description: 'Off label',
  })

  const POWER_TO_DEVICE = formatMessage({
    id: 'sensor.powerState',
    defaultMessage: 'Power to Altrac',
    description: 'Power to Altrac label',
  })

  const RUN = formatMessage({
    id: 'rules.machineState.run',
    defaultMessage: 'RUN',
    description: 'Run label',
  })

  const SHOULD_RUN = formatMessage({
    id: 'rules.machineState.should.run',
    defaultMessage: 'SHOULD RUN',
    description: 'Should Run label',
  })

  const STOP = formatMessage({
    id: 'rules.machineState.stop',
    defaultMessage: 'STOP',
    description: 'Stop label',
  })

  const isPump = (value) => /^(pumpRunning|pumpShouldBeRunning|pumpStopped)$/.test(value)

  const pumpTestValues = {
    pumpRunning: RUN,
    pumpShouldBeRunning: SHOULD_RUN,
    pumpStopped: STOP,
  }
  const pumpTestValue = (formula) => pumpTestValues[formula] || RUN

  let rulesList

  if (rules && rules.length) {
    const buttons = rules
      .sort((a, b) => {
        const templateA = a?.rule?.template || ''
        const templateB = b?.rule?.template || ''
        return templateA.localeCompare(templateB)
      })
      .map((rule) => {
        const {
          id: ruleID,
          type,
          rule: {
            formula,
            test,
            valueKey = '',
          },
          transport: {
            method,
          },
        } = rule
        let {
          rule: {
            sensor,
            template = 'general',
            testValue,
            transport: {
              body,
            } = {},
            unit,
          },
        } = rule

        const link = `/${customerID}/devices/${deviceID}/rules/${ruleID}`
        let { icon, message } = templates[template] || templates.general

        if (!icon) {
          icon = {
            binDicator: <Bin />,
            flow: <Flow />,
            coldAirDrain: <ColdAirDrain />,
            moistureSensor: <Moisture low />,
            pump: (<Pump />),
            pumpFrostWater: <PumpFrostWater />,
            valve: <Valve />,
            temperature: <Thermometer />,
            windMachine: <WindMachine />,
          }[deviceType]
        }

        if (type === 'triggerFavorite') {
          if (isPump(template)) {
            const {
              configuration: {
                model,
              },
              physical: {
                primaryPumpLabel = 'Relay 1',
                secondaryPumpLabel = 'Relay 2',
              } = {},
            } = device

            if (model === 'pump2' && isPump(template)) {
              sensor = (test.endsWith('1')) ? primaryPumpLabel : secondaryPumpLabel
            } else {
              sensor = 'Pump'
            }

            testValue = pumpTestValue(formula)
          }

          const buttonText = message
            ? formatMessage(message, { sensor })
            : formatMessage({ id: 'rules.customRule', defaultMessage: 'Custom Rule' })

          return (
            <Link to={link} key={link}>
              <button type='button' className={`${styles.alertButton}`}>
                <div style={{ minWidth: '75px', width: '75px', margin: 'auto 0px' }}>
                  {icon}
                </div>
                <h4 style={{ margin: 'auto 0px auto 12px', textAlign: 'left' }}>
                  {buttonText}
                </h4>
              </button>
            </Link>
          )
        }

        if (type !== 'trigger') {
          return null
        }

        /*
        soil sensor valueKeys
        ---------
        1, 16, moisture
        17, 32, salinity
        33, 48, temperature
        150, 151 pressure switch
        */
        const key = Array.isArray(valueKey) ? valueKey[0] : valueKey
        const telemetry = telemetryTypes.find((x) => x.valueKey === key)

        if (key >= 1 && key <= 16) {
          sensor = SOIL_MOISTURE_SENSOR
        } else if (key >= 17 && key <= 32) {
          sensor = SOIL_SALINITY_SENSOR
        } else if (key >= 33 && key <= 48) {
          sensor = SOIL_TEMPERATURE_SENSOR
        } else if (key == 128 || key === 'VFT') {
          sensor = AMBIENT_TEMPERATURE
          unit = '°'
          template = {
            greaterThan: 'temperatureGreaterThan',
            lessThan: 'temperatureLessThan',
          }[test] || template
        } else if (key == 129) {
          sensor = INTERNAL_BATTERY
          unit = '%'
        } else if (key == 130) {
          sensor = EXTERNAL_BATTERY
        } else if (key == 131) {
          sensor = MACHINE_STATE
        } else if (key == 134) {
          sensor = MACHINE_SPEED
        } else if (key === 'PG') {
          sensor = POWER_TO_DEVICE
          testValue = OFF
        } else if (isPump(formula)) {
          const {
            configuration: {
              model,
            },
            physical: {
              primaryPumpLabel = 'Relay 1',
              secondaryPumpLabel = 'Relay 2',
            } = {},
          } = device

          if (body && body.includes('{{primaryPumpLabel}}')) {
            sensor = primaryPumpLabel
          } else if (body && body.includes('{{secondaryPumpLabel}}')) {
            sensor = secondaryPumpLabel
          } else {
            sensor = 'Pump'
          }

          testValue = pumpTestValue(formula)
        } else if (typeof telemetry === 'object') {
          sensor = sensor || telemetry.label
          unit = telemetry.unit
        }

        let testType

        try {
          const GREATER_THAN = formatMessage({
            id: 'rules.testType.greaterThan',
            defaultMessage: 'Greater Than',
            description: 'Greater Than option',
          })

          const LESS_THAN = formatMessage({
            id: 'rules.testType.lessThan',
            defaultMessage: 'Less Than',
            description: 'Less Than option',
          })

          const testTypeRequired = !isPump(formula)

          testType = testTypeRequired && {
            averageGreaterThan: formatMessage({
              id: 'rules.testType.averageAbove',
              defaultMessage: 'Average Above',
              description: 'average above option',
            }),
            averageLessThan: formatMessage({
              id: 'rules.testType.averageBelow',
              defaultMessage: 'Average Below',
              description: 'average below option',
            }),
            deadDevice: formatMessage({
              id: 'rules.testType.deadDevice',
              defaultMessage: 'Dead Device',
              description: 'Dead Device option',
            }),
            difference: formatMessage({
              id: 'rules.testType.difference',
              defaultMessage: 'Difference',
              description: 'Difference option',
            }),
            equalTo: formatMessage({
              id: 'rules.testType.equalTo',
              defaultMessage: 'Equal To',
              description: 'Equal To option',
            }),
            greaterThan: GREATER_THAN,
            gt: GREATER_THAN,
            lessThan: LESS_THAN,
            lt: LESS_THAN,
            machineOn: formatMessage({
              id: 'rules.testType.machineOn',
              defaultMessage: 'Machine On',
              description: 'Machine On option',
            }),
            schedule: formatMessage({
              id: 'rules.testType.schedule',
              defaultMessage: 'Schedule',
              description: 'Schedule option',
            }),
            trigger: formatMessage({
              id: 'rules.testType.trigger',
              defaultMessage: 'Trigger',
              description: 'Trigger option',
            }),
          }[test]
        } catch (error) {
          console.error(error)
        }

        let returnTestValue = testValue

        switch (valueKey.toString()) {
          case '131':
            if (template === 'bindicator' ||
            template === 'bindicatorGreaterThan' ||
            template === 'bindicatorLessThan') {
              if (testValue > 3) {
                returnTestValue = '3 bin'
                testType = ''
              } else if (testValue > 1) {
                returnTestValue = '2 bin'
                testType = ''
              } else {
                returnTestValue = '1 bin'
                testType = ''
              }
            } else if (template === 'general' && test === 'equalTo') {
              template = 'machineStateEqualTo'
            } else if (template === 'general' && test === 'lessThan') {
              template = 'machineStateLessThan'
            } else if (template === 'general' && test === 'greaterThan') {
              template = 'machineStateGreaterThan'
            }
            break

          case '128':
            if (
              template === 'temperatureLessThan' ||
            template === 'temperatureGreaterThan'
            ) {
              returnTestValue = fromC(testValue, tempConv, 0)
            }
            break
          default:
            break
        }

        if (!sensor && template === 'differenceHigh') {
          sensor = DIFFERENTIAL
        }

        if (template === 'general' && valueKey == 134) {
          template = 'speed'
          unit = 'rpm'
        }

        if (template === 'general' && valueKey == 129) {
          if (test === 'lessThan') {
            template = 'batteryInternal'
          }
          if (test === 'greaterThan') {
            template = 'batteryInternalHigh'
          }
        }

        if (template === 'general' && valueKey == 130) {
          template = 'batteryExternalLessThan'
        }

        if (template === 'general' && valueKey == 145) {
          if (test === 'greaterThan') {
            template = 'pondLevelHigh'
            unit = '%'
          }
          if (test === 'lessThan') {
            template = 'pondLevelLow'
            unit = '%'
          }
        }

        if (template === 'general' && formula === 'temperatureF') {
          if (test === 'equalTo') {
            template = 'temperatureFEqualTo'
          }
          if (test === 'greaterThan') {
            template = 'temperatureFGreaterThan'
          }
          if (test === 'lessThan') {
            template = 'temperatureFLessThan'
          }
        }

        if (template === 'general') {
          message = { id: 'value', defaultMessage: '{sensor} {testType} {testValue}{unit}' }
          if (test === 'greaterThan') {
            icon = <High />
          }
          if (test === 'lessThan') {
            icon = <Low />
          }
        }

        if (/^temperatureLessThan|temperatureGreaterThan$/.test(template)) {
          testValue = fromC(testValue, tempConv, 0)
          unit = '°'
        }

        if (valueKey == 131 && /^bindicator/.test(template)) {
          if (template === 'bindicator' ||
          template === 'bindicatorGreaterThan' ||
          template === 'bindicatorLessThan') {
            if (testValue > 3) {
              testValue = '3 bin'
            } else if (testValue > 1) {
              testValue = '2 bin'
            } else {
              testValue = '1 bin'
            }
          } else if (test === 'lessThan') {
            testValue = STOP
          } else if (test === 'greaterThan') {
            testValue = RUN
          }
        }

        const {
          icon: buttonIcon = icon,
          message: buttonMessage,
        } = templates[template] || templates.general

        const buttonName = formatMessage(buttonMessage, {
          sensor, testType, testValue: returnTestValue || testValue, unit,
        })

        const buttonText = buttonName || `${sensor || valueKey} ${testType} ${returnTestValue}${unit && unit.match(/\w.*/) ? ' ' : ''}${unit || ''}`

        return (
          <Link to={link} key={link}>
            <button type='button' className={styles.alertButton}>
              <div style={{ minWidth: '75px', width: '75px', margin: 'auto 0px' }}>
                {buttonIcon}
              </div>
              <h4 style={{ margin: 'auto 0px auto 12px', textAlign: 'left' }}>
                {buttonText}
              </h4>
            </button>
          </Link>
        )
      })

    rulesList = (
      <div className={styles.pageWidth}>
        <section>
          {buttons}
        </section>
      </div>
    )
  } else if (alertsReady) {
    rulesList = undefined
  } else {
    rulesList = (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }

  const name = deviceNumber || addressAlias || ''
  const title = formatMessage({ id: 'device.rules', defaultMessage: 'Rules' })
  const newRulesLink = `/${customerID}/devices/${deviceID}/rules/new`

  return (
    <div data-name='DeviceRulesPage'>
      <Helmet><title>{title}</title></Helmet>
      <NavigationBar
        backFunc={() => backToDevice(device.physical.deviceType)}
        page={path}
        name={name}
        logOut={() => logout(navigate)}
        rightControl={{
          icon: rightText,
          func: rightFunction,
        }}
      />
      <div className={styles.pageWidth}>
        <div className={styles.pageContainer}>
          <Link to={newRulesLink} key={newRulesLink}>
            <button type='button' className={`${styles.alertButton}`}>
              <div style={{ width: '75px', margin: 'auto 0px' }}>
                <PlusCircle />
              </div>
              <h4 style={{ margin: 'auto 0px auto 12px', textAlign: 'left' }}>New Rule</h4>
            </button>
          </Link>
          {alertsReady && rules.length > 0 && (
            <section>
              <div className={styles.sectionName}>
                <FormattedMessage
                  id='rules.alert.me'
                  defaultMessage='Alert Me When'
                  tagName='h2'
                />
              </div>
            </section>
          )}
          {rulesList}
        </div>
      </div>
      <Footer />
    </div>
  )
}

DeviceRulesPage.propTypes = {
  route: PropTypes.object,
  data: PropTypes.object,
  auth: PropTypes.object,
  intl: PropTypes.object,
  level: PropTypes.number,
  tempConv: PropTypes.string,
}

export default withAppStore(injectIntl(DeviceRulesPage))
